import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import T2TTopNav        from "../Layout/T2TTopNav";
import ViewSwitcher     from "./ViewSwitcher";
import QuickFilterChips from "./QuickFilterChips";
import FilterBar        from "./FilterBar";
import FilterPanel      from "./FilterPanel";
import ListView         from "./ListView";
import SlideOverPanel   from "./SlideOverPanel";
import BulkActionsBar   from "./BulkActionsBar";
import ColumnPicker, { DEFAULT_VISIBLE } from "./ListView/ColumnPicker";
import SavedViewsDropdown from "./SavedViews/SavedViewsDropdown";
import KanbanBoard from "../Kanban/KanbanBoard";
import CalendarView from "../Calendar/CalendarView";
import NewRequestModal from "./NewRequestModal";
import { fetchRequestList, exportRequestsUrl } from "../../../api/t2tRequestsApi";
import { putData, deleteDatawithBody } from "../../../FetchApiServices";
import { usePermissions, useToast } from "../Layout/T2TUIProvider";
import "./RequestsPage.css";

const DEBOUNCE_MS = 350;

const EMPTY_FILTERS = {
  status: "", priority: "", workflow_id: "", request_type_id: "", date_from: "", date_to: "",
};

const GROUP_BY_OPTIONS = [
  { value: "none",     label: "No grouping" },
  { value: "stage",    label: "By Stage" },
  { value: "status",   label: "By Status" },
  { value: "assignee", label: "By Assignee" },
];

export default function RequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const toast = useToast();
  const authUser  = useSelector(state => state?.user?.users?.[0]);
  const canDelete = hasPermission("requests.delete") || !!authUser?.is_admin || !!authUser?.is_superadmin;

  const [activeView,      setActiveView]      = useState("list");
  const [activeChip,      setActiveChip]      = useState("all");
  const [search,          setSearch]          = useState("");
  const [sortBy,          setSortBy]          = useState("created_at");
  const [sortDir,         setSortDir]         = useState("DESC");
  const [visibleCols,     setVisibleCols]     = useState(DEFAULT_VISIBLE);
  const [groupBy,         setGroupBy]         = useState("none");
  const [rows,            setRows]            = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [selectedId,      setSelectedId]      = useState(null);   // slide-over single row
  const [selectedIds,     setSelectedIds]     = useState(new Set()); // bulk checkboxes
  const [countRefresh,    setCountRefresh]    = useState(0);
  const [filters,         setFilters]         = useState(EMPTY_FILTERS);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [activeViewId,    setActiveViewId]    = useState(null);
  const [newReqOpen,      setNewReqOpen]      = useState(false);

  const debounceRef = useRef(null);

  // URL deep-link: ?id=REQ-042 → auto-open slide-over on mount
  // URL deep-link: ?new=vendor_creation → auto-open New Request modal
  const [newReqAutoCode,    setNewReqAutoCode]    = useState(null);
  const [newReqExtraParams, setNewReqExtraParams] = useState({});

  // Re-run on every URL change (not just mount) so a notification click while
  // already on /t2t/requests still opens the slide-over for the new id.
  useEffect(() => {
    const id       = searchParams.get("id");
    const newCode  = searchParams.get("new");
    const vendorId = searchParams.get("vendor_id");
    if (id) {
      const numeric = parseInt(String(id).replace(/^REQ-/i, ""), 10);
      if (!isNaN(numeric)) setSelectedId(numeric);
    }
    if (newCode) {
      const extra = {};
      if (vendorId) extra.vendor_id = vendorId;
      setNewReqAutoCode(newCode);
      setNewReqExtraParams(extra);
      setNewReqOpen(true);
      // Remove ?new param from URL without re-rendering
      setSearchParams(prev => { prev.delete("new"); prev.delete("vendor_id"); return prev; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Sync ?id param when slide-over opens/closes
  useEffect(() => {
    if (selectedId) {
      setSearchParams(prev => { prev.set("id", `REQ-${String(selectedId).padStart(3, "0")}`); return prev; }, { replace: true });
    } else {
      setSearchParams(prev => { prev.delete("id"); return prev; }, { replace: true });
    }
  }, [selectedId, setSearchParams]);

  const buildParams = useCallback(() => {
    const p = { sort_by: sortBy, sort_dir: sortDir };
    if (search)                                          p.search           = search;
    if (activeChip === "my_queue")                       p.my_queue         = "true";
    if (activeChip === "sla_breach")                     p.sla_breach       = "true";
    if (activeChip === "due_today")                      p.due_today        = "true";
    // BUG-69: High Priority chip now uses the dedicated backend param that
    // matches critical OR high (not just high). Side filter still uses priority.
    if (activeChip === "high_priority")                  p.high_priority    = "true";
    if (filters.status)                                  p.status           = filters.status;
    if (filters.priority)                                p.priority         = filters.priority;
    if (filters.workflow_id)                             p.workflow_id      = filters.workflow_id;
    if (filters.request_type_id)                         p.request_type_id  = filters.request_type_id;
    if (filters.date_from)                               p.date_from        = filters.date_from;
    if (filters.date_to)                                 p.date_to          = filters.date_to;
    return p;
  }, [search, sortBy, sortDir, activeChip, filters]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const resp = await fetchRequestList(buildParams());
    if (resp.error) {
      setError(resp.error?.message || "Failed to load requests.");
    } else {
      setRows(resp.data?.data?.rows || []);
    }
    setLoading(false);
  }, [buildParams]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadData, search ? DEBOUNCE_MS : 0);
    return () => clearTimeout(debounceRef.current);
  }, [loadData, search]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    else { setSortBy(col); setSortDir("DESC"); }
  };

  const handleChipChange = (chip) => { setActiveChip(chip); setSearch(""); };

  const handleRefresh = () => { loadData(); setCountRefresh((n) => n + 1); };

  const handleFilterRemove = (key) => setFilters(prev => ({ ...prev, [key]: "" }));

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // ── Multi-select ──────────────────────────────────────────────────────────────
  const handleCheckboxChange = (requestId, checked) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      checked ? next.add(requestId) : next.delete(requestId);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? new Set(rows.map(r => r.request_id)) : new Set());
  };

  // ── CSV export (browser-side, selected rows only) ─────────────────────────────
  const handleExportCsv = () => {
    const target = selectedIds.size > 0 ? rows.filter(r => selectedIds.has(r.request_id)) : rows;
    const headers = ["Ref #", "Title", "Workflow", "Stage", "Status", "Priority", "Submitted By", "Due Date", "Submitted On"];
    const csvRows = [
      headers.join(","),
      ...target.map(r => [
        r.ref_id, r.title, r.workflow_name, r.stage_name || "",
        (r.status || "").replace(/_/g, " "), r.priority || "",
        r.submitted_by_name || "",
        r.due_at ? new Date(r.due_at).toLocaleDateString("en-IN") : "",
        r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : "",
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Excel export (server-side, current filters applied) ──────────────────────
  const handleExportExcel = () => {
    const url = exportRequestsUrl(buildParams());
    // Use axiosInstance base or fallback to proxy
    window.open(`/api/${url}`, "_blank");
  };

  // ── Bulk set priority ─────────────────────────────────────────────────────────
  const handleSetPriority = async (priority) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const results = await Promise.all(ids.map(id => putData(`t2t/requests/${id}/priority`, { priority })));
      const failed = results.filter(r => r?.error);
      if (failed.length) {
        toast(`Failed to update ${failed.length} request(s).`, "error");
      } else {
        setRows(prev => prev.map(r => selectedIds.has(r.request_id) ? { ...r, priority } : r));
        setSelectedIds(new Set());
        toast(`Priority set to ${priority} for ${ids.length} request(s).`, "success");
      }
    } catch (_) {
      toast("Priority update failed.", "error");
    }
  };

  // ── Bulk delete ───────────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const confirmed = window.confirm(
      `Delete ${ids.length} request${ids.length > 1 ? "s" : ""}? This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      await deleteDatawithBody("t2t/requests/bulk", { ids });
      setRows(prev => prev.filter(r => !selectedIds.has(r.request_id)));
      setSelectedIds(new Set());
    } catch (_) { window.alert("Delete failed. Some requests may not have been deleted."); }
  };

  // ── Saved view apply ──────────────────────────────────────────────────────────
  const handleApplySavedView = (view) => {
    setActiveViewId(view.id);
    const cfg = view.view_config || {};
    if (cfg.filters)     setFilters({ ...EMPTY_FILTERS, ...cfg.filters });
    if (cfg.sortBy)      setSortBy(cfg.sortBy);
    if (cfg.sortDir)     setSortDir(cfg.sortDir);
    if (cfg.activeView)  setActiveView(cfg.activeView);
    if (cfg.visibleCols) setVisibleCols(cfg.visibleCols);
    if (cfg.search)      setSearch(cfg.search || "");
    if (cfg.groupBy)     setGroupBy(cfg.groupBy || "none");
  };

  const currentConfig = { filters, sortBy, sortDir, activeView, visibleCols, search, groupBy };

  return (
    <div className="d-flex flex-column" style={{ flex: 1, minHeight: 0, overflow: "hidden", background: "var(--t2t-bg)" }}>
      <T2TTopNav
        title="Requests"
        actions={
          <button
            className="t2t-btn t2t-btn-primary t2t-btn-sm d-flex align-items-center gap-1"
            onClick={() => setNewReqOpen(true)}
          >
            <i className="bi bi-plus-lg" />
            New Request
          </button>
        }
      />

      {/* Sub-header: quick filters + controls + search */}
      <div className="rp-subheader">
        <div className="rp-subheader-row1">
          <QuickFilterChips
            activeChip={activeChip}
            onChange={handleChipChange}
            refreshTrigger={countRefresh}
          />
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Group By selector */}
            <select
              className="rp-groupby-select"
              value={groupBy}
              onChange={e => setGroupBy(e.target.value)}
              title="Group by"
            >
              {GROUP_BY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <SavedViewsDropdown
              activeViewId={activeViewId}
              onApply={handleApplySavedView}
              currentConfig={currentConfig}
            />
            <ViewSwitcher activeView={activeView} onChange={setActiveView} />
          </div>
        </div>
        <div className="rp-subheader-row2">
          <FilterBar
            search={search}
            onSearchChange={setSearch}
            onRefresh={handleRefresh}
            loading={loading}
            filters={filters}
            onFilterRemove={handleFilterRemove}
            onFiltersOpen={() => setFilterPanelOpen(true)}
            activeFilterCount={activeFilterCount}
            rightSlot={
              activeView === "list" && (
                <ColumnPicker visibleCols={visibleCols} onChange={setVisibleCols} />
              )
            }
          />
        </div>
      </div>

      {/* Content area */}
      <div className="t2t-content-fill">
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {activeView === "list" && (
            <ListView
              rows={rows}
              loading={loading}
              error={error}
              visibleCols={visibleCols}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSort}
              groupBy={groupBy}
              selectedId={selectedId}
              onRowClick={(row) => setSelectedId(row.request_id)}
              selectedIds={selectedIds}
              onCheckboxChange={handleCheckboxChange}
              onSelectAll={handleSelectAll}
            />
          )}
          {activeView === "kanban" && <KanbanBoard />}
          {activeView === "calendar" && (
            <CalendarView onRequestClick={(id) => setSelectedId(id)} />
          )}
        </div>

        {/* Bulk actions bar — sticky bottom */}
        <BulkActionsBar
          selectedCount={selectedIds.size}
          onClearSelection={() => setSelectedIds(new Set())}
          onExportCsv={handleExportCsv}
          onExportExcel={handleExportExcel}
          onSetPriority={handleSetPriority}
          onDelete={handleBulkDelete}
          canDelete={canDelete}
        />
      </div>

      {/* Slide-over panel */}
      {selectedId && (
        <SlideOverPanel
          requestId={selectedId}
          initialTab={searchParams.get("tab") || "details"}
          onClose={() => setSelectedId(null)}
        />
      )}

      {/* Filter panel drawer */}
      <FilterPanel
        show={filterPanelOpen}
        filters={filters}
        onChange={setFilters}
        onClose={() => setFilterPanelOpen(false)}
      />

      {/* New Request modal */}
      {newReqOpen && (
        <NewRequestModal
          onClose={() => { setNewReqOpen(false); setNewReqAutoCode(null); setNewReqExtraParams({}); }}
          autoCode={newReqAutoCode}
          extraParams={newReqExtraParams}
        />
      )}
    </div>
  );
}
